import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Button, CabinetTablePageSkeleton } from '@shared/ui';
import type { Invoice } from '@entities/invoice';
import { useInvoicesList } from '@features/invoices';
import { useInvoiceStats } from '@features/invoices';
import { useInvoiceDetail } from '@features/invoices';
import { useProjects } from '@features/projects';
import { useCurrencies } from '@features/projects';
import { InvoiceStatsCards } from '@widgets/invoices/invoice-stats-cards';
import { InvoicesTable } from '@widgets/invoices/invoices-table';
import { InvoiceInfoModal } from '@widgets/invoices/invoice-info-modal';
import { InvoiceCreateModal } from '@widgets/invoices/invoice-create-modal';

export function PaymentsPage() {
  const { t } = useTranslation('cabinet');
  const [page, setPage] = useState(1);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { invoices, pagination, isLoading, mutate } = useInvoicesList(page, 10);
  const { stats, isLoading: isStatsLoading, mutate: mutateStats } = useInvoiceStats();
  const { projects, isLoading: isProjectsLoading } = useProjects();
  const { currencies, isLoading: isCurrenciesLoading } = useCurrencies();
  const { invoice: selectedInvoice, isLoading: isDetailLoading } =
    useInvoiceDetail(selectedInvoiceId);

  const projectNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const project of projects) {
      map[project.id] = project.name;
    }
    return map;
  }, [projects]);

  const networkCodeByCurrencyId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const currency of currencies) {
      if (currency.network?.code) map[String(currency.id)] = currency.network.code;
    }
    return map;
  }, [currencies]);

  const handleRowClick = (invoice: Invoice) => {
    setSelectedInvoiceId(invoice.id);
  };

  const handleRefresh = () => {
    void mutate();
    void mutateStats();
  };

  const handleCreateSuccess = () => {
    void mutate();
    void mutateStats();
  };

  if (isLoading && !invoices.length && isStatsLoading) {
    return <CabinetTablePageSkeleton />;
  }

  return (
    <div className="page-stack">
      <div className="surface-card flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <p className="font-sans text-sm leading-snug text-foreground">{t('payments.info')}</p>
        <Button
          variant="default"
          className="page-action-button w-full shrink-0 sm:w-auto sm:max-w-xs"
          onClick={() => setIsCreateOpen(true)}
        >
          <Plus className="mr-1.5 h-4 w-4" />
          {t('payments.add')}
        </Button>
      </div>

      <InvoiceStatsCards stats={stats} isLoading={isStatsLoading} />

      <InvoicesTable
        invoices={invoices}
        projectNameById={projectNameById}
        networkCodeByCurrencyId={networkCodeByCurrencyId}
        pagination={pagination}
        isLoading={isLoading || isProjectsLoading}
        onRefresh={handleRefresh}
        onPageChange={setPage}
        onRowClick={handleRowClick}
      />

      <InvoiceInfoModal
        invoice={selectedInvoice}
        isLoading={isDetailLoading}
        open={Boolean(selectedInvoiceId)}
        onOpenChange={(open) => {
          if (!open) setSelectedInvoiceId(null);
        }}
      />

      <InvoiceCreateModal
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        projects={projects}
        currencies={currencies}
        isMetaLoading={isProjectsLoading || isCurrenciesLoading}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
}
